import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, gql } from '@apollo/client';
import SalesOrderDocuments from './SalesOrderDocuments';
import ApiError from '@/lib/shared-coms/ApiError';
import { Spinner } from 'equasion-lib';
import { useRootStore } from '@/lib/providers/RootProvider';
import { useRxPrintEnabled, useSendDocumentsToPrinter } from '@/lib/rxPrint';
import { shipPhaseFor, useShipRun, watchShipRun } from '@/lib/shipRun';

// Rx Exempt is unserialized — do NOT add it here.
const DEA_SCHEDULED = ['Rx Drugs', 'CV', 'CIV', 'CIII', 'CII', 'CI'];
// Poll fast while documents normally arrive within seconds, then back off —
// large orders (5k+ serialized items) take many minutes to render T4/EPCIS.
const POLL_INTERVAL_FAST = 500;
const POLL_INTERVAL_SLOW = 3000;
const POLL_SLOWDOWN_AFTER = 30 * 1000;
const POLL_TIMEOUT = 30 * 60 * 1000;
// A poll request older than this is presumed wedged (device slept mid-request,
// dropped connection — the HTTP link has no timeout) and is superseded.
const POLL_REQUEST_DEADLINE = 15 * 1000;
// While the ship request is still out, only the carrier label can appear (it is filed before the
// ship commits), so the page checks once a second rather than hammering the full documents query
// alongside the ship transaction it is waiting on. The fast window starts when the request settles.
const POLL_INTERVAL_SHIP_RUNNING = 1000;
// An order at `shipping` that THIS device is not shipping (reloaded mid-ship, started elsewhere, or
// a request whose answer was lost): a ship finishes in seconds, so check for a few minutes, then
// stop and offer Check Again rather than polling for half an hour.
const POLL_INTERVAL_PENDING = 1500;
const POLL_TIMEOUT_PENDING = 3 * 60 * 1000;
// `shipping` too: "Generate Labels" lands here the moment it is tapped, while the order is still
// shipping, and the carrier label is filed before the ship commits — so it is shown from then on.
export const DOCUMENT_PAGE_VENDOR_STATUSES = ['shipping', 'shipped', 'completed'];

export const CLIENT_ORDER = gql`
  query salesOrder($id: ID!, $vendorId: ID!, $vendorStatuses: [String!]) {
    salesOrder(id: $id, vendorId: $vendorId, vendorStatuses: $vendorStatuses) {
      id
      vendorSalesOrderNumber
      vendorStatus
      isDestructionOrder
      # Puerto Rico shipments: Ship files a Certificate of Origin with the other documents.
      shipsToPuertoRico
      shippingMethod {
        id
        carrier
      }
      # The parcels it shipped in, and whether their box sizes can be corrected (Box Sizes page).
      shipPackages {
        index
        packageType
        dimensions
      }
      shipPackagesEditable
      # Bundled shipping — the members that travelled in the same carton. Each has its OWN copies
      # of every document, so the print page just links across; nothing is bundle-scoped.
      shippingBundle {
        id
        bundleNumber
        status
        orders {
          id
          vendorSalesOrderNumber
          vendorStatus
        }
      }
      orderItems {
        id
        quantity
        product {
          id
          title
          fdaProduct {
            id
            name
            elevenDigitNdc
            deaSchedule
          }
        }
      }
      shippingLabelDocuments(companyId: $vendorId) {
        id
        filename
        link
        descriptor
      }
      t3ReportDocument(companyId: $vendorId) {
        id
        filename
        link
        descriptor
      }
      t4ReportDocument(companyId: $vendorId) {
        id
        filename
        link
        descriptor
      }
      packingListDocument(companyId: $vendorId) {
        id
        filename
        link
        descriptor
      }
      invoiceDocument(companyId: $vendorId) {
        id
        filename
        link
        descriptor
      }
      stickersDocument(companyId: $vendorId) {
        id
        filename
        humanFilename
        link
        descriptor
      }
      ssccLabelDocument(companyId: $vendorId) {
        id
        filename
        link
        descriptor
      }
      certificateOfOriginDocument(companyId: $vendorId) {
        id
        filename
        humanFilename
        link
        descriptor
      }
    }
  }
`;

const SalesOrderDocumentsData = (props) => {
  const { id } = useParams();
  const vendorId = useRootStore((state) => state.selectedCompany.id);
  const { data, error, loading, refetch } = useQuery(CLIENT_ORDER, {
    variables: { id, vendorId, vendorStatuses: DOCUMENT_PAGE_VENDOR_STATUSES },
    fetchPolicy: 'network-only',
    // Without this, Apollo dedupes an identical refetch onto the still-pending
    // link request (inFlightLinkObservables) — so a request that never settles
    // (device slept mid-request; the HTTP link has no timeout) would absorb
    // every subsequent refetch and the wedge this polling exists to break
    // would persist anyway.
    context: { queryDeduplication: false }
  });
  const [timedOut, setTimedOut] = useState(false);
  const { send: sendToPrinter, sendingKey } = useSendDocumentsToPrinter(vendorId as string);
  // Read once, not on every poll of the documents (see @/lib/rxPrint).
  const rxPrintEnabled = useRxPrintEnabled(vendorId as string);
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  // Render from the last good result: a poll that returns GraphQL errors
  // (errorPolicy 'none' clears data) or a transient salesOrder: null must not
  // blank the page or kill the poll loop mid-wait.
  const lastSalesOrderRef = useRef<any>(null);
  // …but never ACROSS orders. The bundle strip navigates between sibling members of one shipment
  // on this same route, and while the sibling's query is in flight the previous order's header and
  // print links must not stay on screen as if they were the sibling's.
  if (lastSalesOrderRef.current && String(lastSalesOrderRef.current.id) !== String(id)) {
    lastSalesOrderRef.current = null;
  }
  if (data?.salesOrder) lastSalesOrderRef.current = data.salesOrder;
  const salesOrder = data?.salesOrder ?? lastSalesOrderRef.current;

  // The ship this device started for this order, if any (see @/lib/shipRun).
  const shipRun = useShipRun(id);
  const shipPhase = shipPhaseFor(salesOrder?.vendorStatus, shipRun);
  useEffect(() => (id ? watchShipRun(id) : undefined), [id]);
  // The moment the request settles, read the order again rather than waiting for the next tick: a
  // success flips the page to shipped, and a failure stops the waiting immediately.
  useEffect(() => {
    if (shipRun && shipRun.status !== 'running') refetchRef.current().catch(() => undefined);
  }, [shipRun?.status]);

  const hasScheduledItems = salesOrder?.orderItems?.some((item) =>
    DEA_SCHEDULED.includes(item.product?.fdaProduct?.deaSchedule)
  );
  // Destruction orders never generate SSCC/T4 documents (orderShip skips them),
  // so don't make the readiness gate wait for documents that will never come.
  const expectScheduledDocs = !!hasScheduledItems && !salesOrder?.isDestructionOrder;
  // Only a carrier (UPS/FedEx) ship buys a shipping label. FOB (no method at all) and freight
  // (carrier 'freight') record a manual tracking number instead, so waiting on a label document
  // that will never be created would keep this page spinning for the whole poll window and then
  // report a timeout on a shipment that actually succeeded.
  // Mid-ship, the request says: the operator may have picked a different method on the Shipping page
  // than the one the order was placed with (an FOB order shipped with a carrier label in a bundle).
  const expectsCarrierLabel =
    shipRun && shipPhase !== 'shipped'
      ? shipRun.buysCarrierLabel
      : !!salesOrder?.shippingMethod?.id && salesOrder.shippingMethod.carrier !== 'freight';

  // A shipment to Puerto Rico also files a Certificate of Origin (a background job, like the rest).
  const expectsCertificateOfOrigin =
    !!salesOrder?.shipsToPuertoRico && !salesOrder?.isDestructionOrder;

  const allDocumentsReady = !!(
    salesOrder?.packingListDocument &&
    salesOrder?.invoiceDocument &&
    (!expectsCertificateOfOrigin || salesOrder?.certificateOfOriginDocument) &&
    (!expectsCarrierLabel || salesOrder?.shippingLabelDocuments?.length > 0) &&
    (!expectScheduledDocs ||
      (salesOrder?.ssccLabelDocument && salesOrder?.t4ReportDocument))
  );

  // A failed ship produces nothing more to wait for; the page says what happened instead.
  const shouldPoll = !!salesOrder && !allDocumentsReady && !timedOut && shipPhase !== 'failed';
  // Each mode restarts the loop below with its own cadence and window.
  const pollMode: 'ship' | 'pending' | 'documents' =
    shipPhase === 'running' ? 'ship' : shipPhase === 'pending' ? 'pending' : 'documents';
  // A new mode gets a fresh window (e.g. the order flips from pending to shipped).
  useEffect(() => setTimedOut(false), [pollMode]);

  useEffect(() => {
    if (!shouldPoll) return undefined;

    // Self-managed polling instead of Apollo's startPolling: Apollo's poll
    // loop skips every tick while it considers a request in flight, and the
    // HTTP link has no timeout — so one request that never settles (device
    // locked mid-request, dropped warehouse wifi) silently kills all future
    // polls while the UI still says "Generating documents...". refetch()
    // always issues a fresh request, superseding a wedged one.
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let attemptSeq = 0;
    let attemptStartedAt = 0;
    const startedAt = Date.now();

    const schedule = () => {
      if (cancelled) return;
      const interval =
        pollMode === 'ship'
          ? POLL_INTERVAL_SHIP_RUNNING
          : pollMode === 'pending'
          ? POLL_INTERVAL_PENDING
          : Date.now() - startedAt > POLL_SLOWDOWN_AFTER
          ? POLL_INTERVAL_SLOW
          : POLL_INTERVAL_FAST;
      timer = setTimeout(tick, interval);
    };

    const tick = () => {
      if (cancelled) return;
      const now = Date.now();
      const requestInFlight =
        attemptStartedAt && now - attemptStartedAt < POLL_REQUEST_DEADLINE;
      if (!requestInFlight) {
        const attempt = ++attemptSeq;
        attemptStartedAt = now;
        refetchRef
          .current()
          .catch(() => {
            // Transient failure — the next tick retries.
          })
          .finally(() => {
            if (attempt === attemptSeq) attemptStartedAt = 0;
          });
      }
      schedule();
    };

    // When the app returns to the foreground (locked device / backgrounded
    // Capacitor webview), abandon any wedged request and poll immediately.
    // visibilitychange and focus often BOTH fire on resume — only treat an
    // attempt as wedged if it predates this wake, so the second event doesn't
    // immediately duplicate the request the first one just issued.
    const onWake = () => {
      if (document.visibilityState !== 'visible') return;
      if (attemptStartedAt && Date.now() - attemptStartedAt > 2000) {
        attemptStartedAt = 0;
      }
      if (timer) clearTimeout(timer);
      tick();
    };

    schedule();
    document.addEventListener('visibilitychange', onWake);
    window.addEventListener('focus', onWake);
    const pollWindowTimer = setTimeout(
      () => setTimedOut(true),
      pollMode === 'pending' ? POLL_TIMEOUT_PENDING : POLL_TIMEOUT
    );

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      clearTimeout(pollWindowTimer);
      document.removeEventListener('visibilitychange', onWake);
      window.removeEventListener('focus', onWake);
    };
  }, [shouldPoll, pollMode]);

  if (loading && !salesOrder) return <Spinner />;
  // Only hard-fail when we have nothing to show — a transient poll failure
  // must not replace the documents page mid-wait.
  if (error && !salesOrder) return <ApiError error={error} />;

  return (
    <SalesOrderDocuments
      salesOrder={salesOrder}
      isPolling={shouldPoll}
      shipPhase={shipPhase}
      shipRun={shipRun}
      generationTimedOut={timedOut && !allDocumentsReady}
      // The pending-ship panel has its own Check Again once its shorter window runs out.
      pendingCheckTimedOut={timedOut && shipPhase === 'pending'}
      onRetryPolling={() => setTimedOut(false)}
      // A reprint files a new stickers document a few seconds later (a background job), so the
      // download button picks up the regenerated file without leaving the page.
      onStickersReprinted={() =>
        [4000, 12000].forEach((ms) =>
          setTimeout(() => refetchRef.current().catch(() => undefined), ms)
        )
      }
      hasScheduledItems={expectScheduledDocs}
      expectsCarrierLabel={expectsCarrierLabel}
      expectsCertificateOfOrigin={expectsCertificateOfOrigin}
      // A regenerated certificate is filed by the request itself; pick it up straight away.
      onCertificateOfOriginGenerated={() => refetchRef.current().catch(() => undefined)}
      rxPrintEnabled={rxPrintEnabled}
      sendingKey={sendingKey}
      onSendToPrinter={(key, documents) => sendToPrinter(key, salesOrder?.id, documents)}
      {...props}
    />
  );
};

export default SalesOrderDocumentsData;
